import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Alert,
  Container,
  Form,
  Input,
  Label,
  FormFeedback,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import logo from '../../assets/images/NGK-LOGO.png';
import CarouselPage from '../AuthenticationInner/CarouselPage';
import {
  beginTotpEnrollment,
  clearPendingTotp,
  confirmTotpEnrollment,
  getPendingTotp,
} from '../../helpers/wordpress_totp';
import { clearPendingEmailOtp } from '../../helpers/wordpress_auth';
import { loginSuccess } from '../../store/actions';
import { getAuthUserString } from '../../helpers/auth_storage';

const TotpSetup = () => {
  document.title = 'Set Up Authenticator | NGK Tours';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  const pending = useMemo(() => getPendingTotp(), []);
  const isLoginSetup = pending?.mode === 'setup';

  useEffect(() => {
    const run = async () => {
      try {
        if (isLoginSetup) {
          if (!pending?.challengeToken) {
            navigate('/login', { replace: true });
            return;
          }
          setChallengeToken(pending.challengeToken);
          const started = await beginTotpEnrollment(pending.challengeToken);
          setOtpauthUrl(started.otpauthUrl);
          setSecret(started.secret);
          setRecoveryCodes(started.recoveryCodes);
        } else {
          // Authenticated profile setup
          const authUser = getAuthUserString();
          if (!authUser) {
            navigate('/login', { replace: true });
            return;
          }
          const started = await beginTotpEnrollment(null);
          setOtpauthUrl(started.otpauthUrl);
          setSecret(started.secret);
          setRecoveryCodes(started.recoveryCodes);
        }
      } catch (err: any) {
        setError(err?.message || 'Unable to start authenticator setup.');
      } finally {
        setLoadingSetup(false);
      }
    };
    run();
  }, [isLoginSetup, pending, navigate]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { code: '' },
    validationSchema: Yup.object({
      code: Yup.string()
        .matches(/^\d{6}$/, 'Enter the 6-digit code from your app')
        .required('Enter the 6-digit code from your app'),
    }),
    onSubmit: async (values) => {
      setError(null);
      setSubmitting(true);
      try {
        const result = await confirmTotpEnrollment(
          values.code,
          isLoginSetup ? challengeToken : null
        );
        if (result.status === 'authenticated') {
          dispatch(loginSuccess(result.authData));
          navigate('/dashboard');
          return;
        }
        if (result.status === 'setup_complete') {
          navigate('/security-settings?totp=1');
          return;
        }
        setError('Unexpected setup response.');
      } catch (err: any) {
        setError(err?.message || 'Setup confirmation failed.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const qrSrc = otpauthUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(otpauthUrl)}`
    : '';

  return (
    <React.Fragment>
      <div className="auth-page">
        <Container fluid className="p-0">
          <Row className="g-0">
            <Col lg={4} md={5} className="col-xxl-3">
              <div className="auth-full-page-content d-flex p-sm-5 p-4">
                <div className="w-100">
                  <div className="d-flex flex-column h-100">
                    <div className="mb-4 mb-md-5 text-center">
                      <Link to="/login" className="d-block auth-logo">
                        <img src={logo} alt="NGK Tours" height="28" />
                      </Link>
                    </div>
                    <div className="auth-content my-auto">
                      <div className="text-center mb-3">
                        <h4>Set up authenticator 2FA</h4>
                        <p className="text-muted mb-0">
                          Required for all staff. Scan the QR code with Google
                          Authenticator, Authy, or similar.
                        </p>
                      </div>

                      {error && <Alert color="danger">{error}</Alert>}
                      {loadingSetup && (
                        <Alert color="info">Preparing authenticator setup...</Alert>
                      )}

                      {!loadingSetup && otpauthUrl && (
                        <>
                          <div className="text-center mb-3">
                            <img
                              src={qrSrc}
                              alt="Authenticator QR code"
                              width={180}
                              height={180}
                            />
                            <p className="small text-muted mt-2 mb-0">
                              Or enter secret manually:
                            </p>
                            <code className="small">{secret}</code>
                          </div>

                          {recoveryCodes.length > 0 && (
                            <Alert color="warning" className="small">
                              <strong>Save these recovery codes</strong> in a
                              safe place. Each works once if you lose your phone.
                              <ListGroup flush className="mt-2 mb-0">
                                {recoveryCodes.map((c) => (
                                  <ListGroupItem
                                    key={c}
                                    className="py-1 px-0 bg-transparent border-0 font-monospace small"
                                  >
                                    {c}
                                  </ListGroupItem>
                                ))}
                              </ListGroup>
                            </Alert>
                          )}

                          <Form
                            onSubmit={(e) => {
                              e.preventDefault();
                              validation.handleSubmit();
                              return false;
                            }}
                          >
                            <div className="mb-3">
                              <Label className="form-label">
                                Confirm with app code
                              </Label>
                              <Input
                                name="code"
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                placeholder="6-digit code"
                                value={validation.values.code}
                                onChange={(e) => {
                                  validation.setFieldValue(
                                    'code',
                                    e.target.value.replace(/\D/g, '').slice(0, 6)
                                  );
                                }}
                                onBlur={validation.handleBlur}
                                invalid={
                                  !!(
                                    validation.touched.code &&
                                    validation.errors.code
                                  )
                                }
                              />
                              {validation.touched.code &&
                              validation.errors.code ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.code}
                                </FormFeedback>
                              ) : null}
                            </div>
                            <div className="d-grid">
                              <button
                                className="btn btn-primary"
                                type="submit"
                                disabled={submitting}
                              >
                                {submitting
                                  ? 'Enabling...'
                                  : 'Enable authenticator & continue'}
                              </button>
                            </div>
                          </Form>
                        </>
                      )}

                      <div className="mt-4 text-center">
                        {isLoginSetup ? (
                          <Link
                            to="/login"
                            onClick={() => {
                              clearPendingTotp();
                              clearPendingEmailOtp();
                            }}
                          >
                            Back to login
                          </Link>
                        ) : (
                          <Link to="/profile">Back to profile</Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <CarouselPage />
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TotpSetup;
